// Region-of-interest for LIVE decoding + the on-screen aiming guide. The decode
// window and the drawn guide come from the SAME fractions, so what the operator
// sees is exactly what the engine reads. Photo uploads are never windowed.
import type { ScanMode } from './decode.js';

// Centered fractions of the intrinsic frame. barcode: wide + short (a 1D
// strip); qr: room for a square symbol; both: generous enough for either.
const ROI: Record<ScanMode, { w: number; h: number }> = {
  barcode: { w: 0.9, h: 0.4 },
  qr: { w: 0.7, h: 0.7 },
  both: { w: 0.9, h: 0.6 },
  isbn: { w: 0.9, h: 0.4 }, // same 1D strip as barcode
};

/**
 * A theme role's colour, read from `<html>` at DRAW time — the canvas cannot
 * inherit a CSS variable, and a literal would be the same green in both
 * themes (audit L5 / COL-12: the scan line was Tailwind red-400 and the
 * tracking box Tailwind green-400, neither a Spectrum stop). `--color-success`
 * and `--color-error` are daisyUI's role vars, re-bound per theme in base.css.
 * The fallback is the light theme's literal for a canvas drawn before the
 * sheet resolves (or under a test DOM with no stylesheet).
 */
export function themeRole(name: "--color-success" | "--color-error"): string {
  const v = typeof getComputedStyle === "function" ? getComputedStyle(document.documentElement).getPropertyValue(name).trim() : "";
  return v || (name === "--color-success" ? "#007a4d" /* green-900, light */ : "#d31510" /* red-900, light */);
}

export function roiRect(mode: ScanMode, vw: number, vh: number): { x: number; y: number; w: number; h: number } {
  const f = ROI[mode];
  const w = vw * f.w, h = vh * f.h;
  return { x: (vw - w) / 2, y: (vh - h) / 2, w, h };
}

/** Draw the aiming window (veil + border + a center "laser" for 1D modes) on an
 * overlay canvas, mapped through the object-fit: cover transform. Sizes and
 * clears the canvas; returns the transform so the caller can layer tracking
 * brackets on top, or null when either element isn't ready. */
export function drawRoiGuide(
  cv: HTMLCanvasElement,
  video: HTMLVideoElement,
  mode: ScanMode,
): { ctx: CanvasRenderingContext2D; scale: number; ox: number; oy: number } | null {
  const cw = cv.clientWidth, ch = cv.clientHeight;
  const vw = video.videoWidth, vh = video.videoHeight;
  if (!cw || !ch || !vw || !vh) return null;
  if (cv.width !== cw) cv.width = cw;
  if (cv.height !== ch) cv.height = ch;
  const ctx = cv.getContext('2d');
  if (!ctx) return null;
  ctx.clearRect(0, 0, cw, ch);
  const scale = Math.max(cw / vw, ch / vh);
  const ox = (cw - vw * scale) / 2;
  const oy = (ch - vh * scale) / 2;
  const r = roiRect(mode, vw, vh);
  const x = r.x * scale + ox, y = r.y * scale + oy, w = r.w * scale, h = r.h * scale;
  ctx.fillStyle = 'rgba(0, 0, 0, 0.28)';
  ctx.fillRect(0, 0, cw, ch);
  ctx.clearRect(x, y, w, h);
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
  ctx.lineWidth = 1.5;
  ctx.strokeRect(x, y, w, h);
  if (mode !== 'qr') {
    // The 1D "laser" in the negative role — the same red the theme uses, opaque
    // over the veil (the veil and the frame are the sanctioned chrome-over-video
    // transparencies; the line is content and takes a stop).
    ctx.strokeStyle = themeRole("--color-error");
    ctx.beginPath();
    ctx.moveTo(x + 10, y + h / 2);
    ctx.lineTo(x + w - 10, y + h / 2);
    ctx.stroke();
  }
  return { ctx, scale, ox, oy };
}
